import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { Res, TicketDTO, TicketLineDTO } from 'core/api';
import { PosScreenClassComponent, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { Col, Cols, ErrorBlock, Flex } from 'core/ui';
import * as PartialRefund from './PartialRefund';
import { CustomError } from 'core/infrastructure';
import { Message, Modal } from 'core/ui-next/modal';
import { TicketControl } from 'pos/ui/TicketControl';
import { Box } from '@chakra-ui/react';

type Props = {
	status: PartialRefund.TicketDivision,
} & Modal.Actions<void>

type State = PartialRefund.TicketDivision & {
	selectedLine?: TicketLineDTO | null;
	selectedSide?: 'Left' | 'Right' | null;
};

type PartialRefundTicketControlProps = {
	ticket: TicketDTO;
	ticketRole: 'Left' | 'Right';
	title: string;
	selectedLine?: Nullable<TicketLineDTO>;
	onSelect: (line: TicketLineDTO | null) => void;
};

interface TicketControlRef {
	getSelectedLineId: () => number | undefined;
	selectLineId: (id: number) => void;
}

const PartialRefundTicketControl = React.forwardRef<TicketControlRef, PartialRefundTicketControlProps>((props, ref) => {

	const { ticket, selectedLine, onSelect, title } = props;
	const [selectedLineId, setSelectedLineId] = React.useState(selectedLine?.Id);
	const hasCustomer = ticket.Customer.Id !== 0;

	React.useImperativeHandle(ref, () => ({
		getSelectedLineId: () => selectedLineId,
		selectLineId: (id: number) => setSelectedLineId(id)
	}));

	React.useEffect(() => {
		setSelectedLineId(selectedLine?.Id);
	}, [selectedLine]);

	return <Flex.Vertical>
		<Flex.Item className='text-26 text-uppercase text-center p-xs'>
			{title}
		</Flex.Item>

		<Flex.Item size='*'>
			<TicketControl
				ticket={ticket}
				selectedLine={ticket.Lines.find(line => line.Id === selectedLineId) ?? null}
				onSelect={line => { setSelectedLineId(line?.Id); onSelect(line); }}
				showTicketStatus={false}
				expandedTicket={false}
			/>
		</Flex.Item>
		<Flex.Item className='p-xs'>
			{
				hasCustomer
					? <div className='text-right m-t-xs'>{ticket.Customer.FiscalName}</div>
					: null
			}
			<div className='text-right font-bold text-26'>
				<span className='font-bold'>{Res.Web.SPLIT_TICKET_TOTAL_LBL(ticket.NetAmount)}</span>
			</div>
		</Flex.Item>
	</Flex.Vertical>;
});

export class PartialRefundScreen extends PosScreenClassComponent<Props, State> {

	private leftTicketControl = React.createRef<TicketControlRef>();
	private rightTicketControl = React.createRef<TicketControlRef>();

	protected getScreenId(): string {
		return 'PARTIAL_REFUND';
	}

	protected getTitle(): string {
		return Res.Web.PARTIAL_REFUND_TITLE();
	}

	protected getImage() {
		return Images.PartialRefund;
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	createInitialState(props: Props): Nullable<State> {
		return { ...props.status };
	}

	protected async requestDismiss(): Promise<void> {
		await this.execute({
			action: async () => {
				await PartialRefund.cancel();
				this.cancel();
			}
		});
	}

	onComponentDidMount(): void {
		this.on('pos:partial-refund:operation-failed', async ({ msg, error }: { msg: string, error: CustomError }) => {

			await Message.show(msg);

			// Si es un error de impresión o es un fallo cuando estoy cerrando la cuenta parcial
			// me quedo en esta pantalla, todo ha ido bien.
			if (!error.isPrintError())
				await PartialRefund.cancel();

			this.cancel();
		});
	}

	onComponentWillUnmount(): void {
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		const onClose = async () => {

			if (this.state.division.Error.length)
				return;

			if (this.state.division.Right.Lines.length === 0)
				return;

			const result = await PartialRefund.refundTicket(this.state);
			if (result.ok)
				this.close();
		};

		const onCancel = async () => this.requestDismiss();

		return [
			this.cancelButton(onCancel),
			null,
			null,
			null,
			null,
			this.okButton(onClose)
		];
	}

	protected getContent(): React.ReactNode {

		const onMoveOneToLeft = async () => {
			if (this.state.selectedSide === 'Right' && this.state.selectedLine) {
				let ticketDivision = this.state;
				let selectedLineIndex = this.state.division.Right.Lines.findIndex(x => x.Id === this.state.selectedLine?.Id);
				if (selectedLineIndex !== -1) {
					ticketDivision = await PartialRefund.moveOneToLeft(this.state, this.state.division.Right.Lines[selectedLineIndex]);
				}
				// Al mover una línea por completo hacia el otro lado, si era la última línea seleccionada, se queda seleccionada una línea que ya no existe, 
				// por lo que selecciono la nueva última línea del lado original
				if (selectedLineIndex === ticketDivision.division.Right.Lines.length) {
					selectedLineIndex = ticketDivision.division.Right.Lines.length - 1;
				}
				const selectedLine = ticketDivision.division.Right.Lines[selectedLineIndex];

				this.setState({ ...ticketDivision, selectedLine: selectedLine });
			}
		};

		const onMoveOneToRight = async () => {
			if (this.state.selectedSide === 'Left' && this.state.selectedLine) {
				let ticketDivision = this.state;
				let selectedLineIndex = this.state.division.Left.Lines.findIndex(x => x.Id === this.state.selectedLine?.Id);
				if (selectedLineIndex !== -1) {
					ticketDivision = await PartialRefund.moveOneToRight(this.state, this.state.division.Left.Lines[selectedLineIndex]);
				}
				// Al mover una línea por completo hacia el otro lado, si era la última línea seleccionada, se queda seleccionada una línea que ya no existe, 
				// por lo que selecciono la nueva última línea del lado original
				if (selectedLineIndex === ticketDivision.division.Left.Lines.length) {
					selectedLineIndex = ticketDivision.division.Left.Lines.length - 1;
				}
				const selectedLine = ticketDivision.division.Left.Lines[selectedLineIndex];

				this.setState({ ...ticketDivision, selectedLine: selectedLine });
			}
		};

		return <Box bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }} paddingX={2} height={'100%'}>
			<Flex.Vertical>
				<Flex.Item size='*'>
					<Flex.Horizontal>
						<Flex.Item size='*'>
							<PartialRefundTicketControl ref={this.leftTicketControl}
								ticket={this.state.division.Left}
								ticketRole='Left'
								title={Res.Web.PARTIAL_REFUND_ORIGINAL_INVOICE_TITLE()}
								selectedLine={this.state.selectedSide === 'Left' ? this.state.selectedLine : null}
								onSelect={line => this.setState({ selectedLine: line, selectedSide: 'Left' })}
							/>
						</Flex.Item>
						<Flex.Item className='m'>
							<Flex.Vertical>
								<Flex.Item size='*' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
									<UI.Button style={{ height: 65, width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onMoveOneToRight}><Images.Right /></UI.Button>
									<UI.Button style={{ height: 65, width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 15 }} onClick={onMoveOneToLeft}><Images.Left /></UI.Button>
								</Flex.Item>
							</Flex.Vertical>
						</Flex.Item>
						<Flex.Item size='*'>
							<PartialRefundTicketControl ref={this.rightTicketControl}
								ticket={this.state.division.Right}
								ticketRole='Left'
								title={Res.Web.PARTIAL_REFUND_TICKET_TITLE()}
								selectedLine={this.state.selectedSide === 'Right' ? this.state.selectedLine : null}
								onSelect={line => this.setState({ selectedLine: line, selectedSide: 'Right' })}
							/>
						</Flex.Item>
					</Flex.Horizontal>
				</Flex.Item >
				{
					this.state.division.Error ?
						<Flex.Item>
							<Cols>
								<Col className='has-error text-right text-18' width='*'>
									<p><ErrorBlock msg={this.state.division.Error} /></p>
								</Col>
							</Cols>
						</Flex.Item>
						: null
				}
			</Flex.Vertical >
		</Box>;
	}
}
